import {
  captureSentryMessage,
  useGqtyClient,
  useMutation,
  useTrackingEvent,
  useTeam,
} from '@vrplatform/data';
import { showWarnNotification } from '@vrplatform/theme';
import type { Maybe } from '@vrplatform/utils';

export function useAutomationDeleteMutation() {
  const track = useTrackingEvent();
  const [{ id: teamId }] = useTeam();

  const client = useGqtyClient();

  const { mutate: removeMutation, loading } = useMutation(
    (q, args: { automationId: string; teamId: string }) => {
      if (!args.automationId) return;

      const count =
        q.delete_setting({
          where: {
            leftType: { _eq: 'automationSetting' },
            automationId: { _eq: args.automationId },
            tenant_id: { _eq: args.teamId },
          },
        }).affected_rows || 0;

      const id = q.deleteAutomation({
        id: args.automationId,
      })?.id;

      return {
        id,
        count,
      };
    },
    {
      successMessage: {
        title: 'Success!',
        message:
          'The automation was deleted successfully. This action cannot be undone.',
      },
      errorMessage: {
        title: 'Failed to delete automation',
        message: (error) => {
          if (
            error.message.includes(
              `delete on table "automation" violates foreign key constraint "owner_statement_automation_id_fkey"`
            )
          ) {
            return 'This automation was used for owners statements. Please remove the statement before deleting.';
          }

          console.error(error);

          return (
            error?.message ||
            'We failed to remove the automation. Please try again.'
          );
        },
      },
      invalidateQueryKeys: ['automations'],
    }
  );

  return {
    mutate: async (
      automationId: string,
      template: {
        id: Maybe<string>;
        type?: Maybe<string>;
        uniqueRef?: Maybe<string>;
      }
    ) => {
      if (!automationId) {
        showWarnNotification({ message: 'Missing automation.' });
        return;
      }

      const mappings = await client.query((q) => {
        return q
          .setting({
            where: {
              leftType: { _neq: 'automationSetting' },
              automationId: { _eq: automationId },
              tenant_id: { _eq: teamId },
            },
          })
          .map((setting) => ({
            id: setting.id,
            key: setting.key,
            target: setting.target,
            value: setting.value,
            leftType: setting.leftType,
            rightType: setting.rightType,
            localAutomationId: setting.localAutomationId,
            automationId: setting.automationId,
            leftConnectionId: setting.leftConnectionId,
            rightConnectionId: setting.rightConnectionId,
          }));
      });

      if (mappings.length > 0) {
        captureSentryMessage(
          `Found mappings with automationId before automation delete: ${automationId}`,
          {
            extra: {
              settings: mappings,
              automationId,
              template,
            },
          }
        );
      }

      const res = await removeMutation({
        args: { automationId, teamId },
      });

      if (res?.id) {
        track('automation_deleted', {
          automationId: automationId,
          templateId: template.id,
          type: template.type,
          uniqueRef: template.uniqueRef,
        });
      }
    },
    loading,
  };
}
